import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';

// Contrat du rig partagé : interdit. Tout est un néon dessiné par distance.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'mandala',
  family: 'shader',
  danceability: 0.88,
  energyLevel: 0.70,
  bpmMin: 60,
  bpmMax: 180,
  movement: 0.70,
  tonality: 0.70
};

// MANDALA : un mandala de néon qui pousse depuis le centre, anneau par
// anneau (pétales, dents, arcs, points, treillis, rayons, vagues), chacun en
// symétrie radiale. Pour tout morceau : il fleurit lentement sur une
// ballade, il claque et tourbillonne sur une techno. Règles de
// kiosk-app/CLAUDE.md (R1 à R19), registres de lib/adapt.js.
//
// UN QUAD PLEIN ÉCRAN. Coordonnées polaires autour du centre de composition
// (R19) ; l'anneau du pixel est choisi par son rayon (une chaîne de
// comparaisons déroulée en JS, sans tableau indexé : V3D lit les tableaux
// d'uniforms par la TMU), plus l'anneau voisin quand le pixel est à moins de
// 10 px d'un bord - deux motifs évalués au plus par pixel. Dans l'anneau,
// l'angle est REPLIÉ sur un secteur de 2pi/N et le motif est une distance
// signée dans le repère (longueur d'arc, rayon) du secteur ; le trait est
// dessiné par sa distance en pixels (R1 : coeur blanc, gaine de couleur,
// halo faible), l'intérieur des formes pleines rempli d'une couleur très
// sombre (luminance 0,08) pour que les couches se superposent proprement.
// Pas de moiré au centre : l'ordre N d'un anneau est borné pour qu'un
// secteur fasse au moins ~22 px de long à son rayon moyen.
//
// LE TROU CENTRAL : autour du bloc de la pochette quand elle est au centre ;
// quand elle est ailleurs (un coin), le mandala part du vrai centre, sans
// trou (comme demandé pour Drones le 15/10/2026).
//
// LA ROTATION, multiple de 2pi/N par mesure (critère 2) : chaque anneau
// avance d'un nombre ENTIER de crans pendant les mesures où il bouge (une
// mesure sur P, P = 4, 2 ou 1 selon adapt.motion ; 2 crans par mesure au
// plus fort), en douceur à l'intérieur de la mesure, et retombe sur sa
// symétrie à chaque temps fort. Les anneaux bougent à tour de rôle (décalés
// d'une mesure), en sens alternés. Au registre intense, sur chaque temps un
// anneau flashe et fait un cran de plus (en 0,15 s).
//
// LA RESPIRATION : une onde radiale (±3 % d'échelle) qui part du centre ;
// ample au calme, discrète au plus fort.
//
// LA POUSSE : au début du morceau et à chaque changement de section, les
// anneaux apparaissent un par un depuis le centre, chacun tracé en
// balayage angulaire (au compas, la pointe lumineuse en tête) ; un anneau
// par temps au plus fort, un toutes les 2 s au calme. Au drop (si le
// morceau en a) : un cran ensemble, un flash, et la pousse refait tout le
// mandala en une mesure.
//
// LES COULEURS : une rampe qui coule vers l'extérieur ; mode profond (indigo,
// or, bordeaux) au calme, couleurs vives de la pochette au plus fort, mêlées
// par adapt.saturation (rien ne saute).

const MAX_RINGS = 10;
const MOTIFS = ['petals', 'teeth', 'arcs', 'dots', 'lattice', 'spikes', 'waves'];
const ORDERS = [6, 8, 12, 16, 24];
const R_OUT = 0.96;                     // 48 % of the screen height (height units: the screen is 2 tall)
const MIN_SECTOR_PX = 22;               // at its mean radius, at 1080 lines
const DEEP = ['#3b2f9e', '#d9a42b', '#8c1c3a', '#5a3fc0', '#c98a1e'];
const FALLBACK_VIVID = ['#ff3fa4', '#2fd6ff', '#ffd23f'];
const SAFE_FLOOR = 0.5;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function sm(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
function hashStr(str) { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }

// ----------------------------------------------------------------- the shader

function fragment() {
  const R = Array.from({ length: MAX_RINGS }, (_, k) => k);
  return `
  precision highp float;
  uniform vec2 uCenter;          // the composition centre (height units)
  uniform float uAspect;
  uniform float uPx;             // height units per pixel
  uniform float uBreathAmp;
  uniform float uBreathPhase;
  uniform float uGain;
  uniform float uCount;
  ${R.map((k) => `uniform vec4 uA${k}; uniform vec4 uB${k}; uniform vec4 uC${k};`).join('\n  ')}
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const float TAU = 6.2831853;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  // iq's isosceles triangle: apex at the origin, base at y = q.y
  float sdTri(vec2 p, vec2 q) {
    p.x = abs(p.x);
    vec2 a = p - q * clamp(dot(p, q) / dot(q, q), 0.0, 1.0);
    vec2 b = p - q * vec2(clamp(p.x / q.x, 0.0, 1.0), 1.0);
    float s = -sign(q.y);
    vec2 d = min(vec2(dot(a, a), s * (p.x * q.y - p.y * q.x)), vec2(dot(b, b), s * (p.y - q.y)));
    return -sqrt(d.x) * sign(d.y);
  }
  // a motif in the sector's frame: q.x along the arc, q.y outward from the
  // ring's mean radius; S the half sector (arc), H the half band. so = 1:
  // a line motif (no fill)
  float motif(float m, vec2 q, float S, float H, out float so) {
    so = 0.0;
    // (petals and teeth keep their width bounded by the band: a wide sector
    // gives spaced petals, never a long flat arc)
    if (m < 0.5) { vec2 e = vec2(min(0.82 * S, 1.1 * H), 0.92 * H); return (length(q / e) - 1.0) * min(e.x, e.y); }  // PETALS
    if (m < 1.5) return sdTri(vec2(q.x, 0.92 * H - q.y), vec2(min(0.9 * S, 1.7 * H), 1.84 * H));                       // TEETH
    if (m < 2.5) { so = 1.0; float k = S / H; return (length(vec2(abs(q.x) - S, q.y * k)) - S) / max(k, 1.0); }          // ARCS
    if (m < 3.5) return length(q) - 0.5 * min(S, H);                                                                      // DOTS
    if (m < 4.5) { so = 1.0; return (abs(q.x) / S + abs(q.y) / H - 1.0) * min(S, H) * 0.7; }                           // LATTICE
    if (m < 5.5) { so = 1.0; return max(abs(q.x), abs(q.y) - 0.9 * H); }                                                // SPIKES
    so = 1.0; return (q.y - 0.55 * H * cos(3.14159265 * q.x / S)) * 0.8;                                                // WAVES
  }
  vec3 ring(float r, float th, vec4 A, vec4 B, vec4 C) {
    float rin = A.x, rout = A.y, N = A.z, m = A.w;
    float rot = B.x, rev = B.y, fl = B.z, st = B.w;
    if (C.a <= 0.001 || rev <= 0.0) return vec3(0.0);
    float rm = 0.5 * (rin + rout), H = 0.5 * (rout - rin) * 0.84;
    float sec = TAU / N;
    float a = mod(th - rot, sec) - 0.5 * sec;
    float S = 0.5 * sec * rm * 0.92;
    float so;
    float d = motif(m, vec2(a * r, r - rm), S, H, so);
    float dpx = (so > 0.5 ? abs(d) : abs(d)) / uPx;
    vec3 col = C.rgb;
    float core = 1.0 - smoothstep(0.5, 1.5, dpx);
    float sheath = 1.0 - smoothstep(2.5, 3.5, dpx);
    float halo = exp(-dpx / 5.0) * 0.25 * (1.0 - smoothstep(8.0, 11.0, dpx));
    vec3 c = col * (0.7 * sheath + halo * (1.0 - sheath));
    c = mix(c, vec3(0.86, 0.84, 0.80) + col * 0.08, core);
    // the dark fill inside the solid shapes (luminance 0.08), only in its band
    float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
    if (so < 0.5 && d < 0.0 && r > rin && r < rout) c = max(c, col * (0.08 / max(lum, 0.08)));
    // the band's outer circle, thin and dim
    float dc = abs(r - rout) / uPx;
    c = max(c, col * 0.3 * (1.0 - smoothstep(0.5, 1.5, dc)));
    // a flash: brighter, toward white on the lines
    c *= 1.0 + 1.1 * fl; c = mix(c, vec3(1.0), 0.3 * fl * sheath);
    // THE COMPASS: drawn up to the sweep angle, a bright point at its tip
    float ang = mod(th - st, TAU), lim = rev * TAU;
    float apx = (lim - ang) * r / uPx;              // pixels behind the tip
    float vis = rev >= 0.999 ? 1.0 : smoothstep(-1.0, 1.0, apx);
    float tip = rev < 0.999 ? exp(-abs(apx) / 6.0) * (1.0 - smoothstep(rout - rin, (rout - rin) * 1.4, abs(r - rm) * 2.0)) : 0.0;
    return (c * vis + col * tip * 0.9) * C.a;
  }
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 p = sp - uCenter;
    float r0 = length(p), th = atan(p.y, p.x);
    // the breath: a radial wave from the centre (the delay grows outward)
    float r = r0 / (1.0 + uBreathAmp * sin(uBreathPhase - r0 * 5.0));
    // the pixel's ring, and its nearer neighbour within 10 px of an edge
    vec4 A = vec4(-1.0), B = vec4(0.0), C = vec4(0.0), A2 = vec4(-1.0), B2 = vec4(0.0), C2 = vec4(0.0);
    float e = 10.0 * uPx;
    ${R.map((k) => `if (${k}.0 < uCount && r >= uA${k}.x - e && r < uA${k}.y + e) { if (A.x < -0.5) { A = uA${k}; B = uB${k}; C = uC${k}; } else { A2 = uA${k}; B2 = uB${k}; C2 = uC${k}; } }`).join('\n    ')}
    vec3 col = vec3(0.0);
    if (A.x > -0.5) col = ring(r, th, A, B, C);
    if (A2.x > -0.5) col = max(col, ring(r, th, A2, B2, C2));
    gl_FragColor = vec4(col * uGain * safeAtt(sp), 1.0);
  }
`;
}
const VERTEX = `
  varying vec2 vUv;
  void main() { vUv = position.xy * 0.5 + 0.5; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

// =============================================================================

export class MandalaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    const sd = parseInt(urlParam('mandalaseed'), 10);
    this._forcedSeed = Number.isFinite(sd) ? sd : null;
    const ov = urlParam('mandalaring');
    this._override = null;
    if (ov) { const [i, m] = ov.split(':'); if (Number.isFinite(+i) && MOTIFS.includes(m)) this._override = { i: +i, m }; }
    this._post = new VdjPost({ bloomStrength: 0.4, bloomThreshold: 0.78, grain: 0.015, vignette: 0.35 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('mandalaprobe') === '1' && typeof window !== 'undefined') window.__mandala = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._adapt = new Adapt();
    const U = this._U = {
      uCenter: { value: new THREE.Vector2() }, uAspect: { value: 1.78 }, uPx: { value: 2 / 1080 },
      uBreathAmp: { value: 0 }, uBreathPhase: { value: 0 }, uGain: { value: 1 }, uCount: { value: 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    for (let k = 0; k < MAX_RINGS; k++) { U['uA' + k] = { value: new THREE.Vector4(-1, -1, 6, 0) }; U['uB' + k] = { value: new THREE.Vector4() }; U['uC' + k] = { value: new THREE.Vector4() }; }
    this._geo = new THREE.PlaneGeometry(2, 2);
    this._mat = new THREE.ShaderMaterial({ uniforms: U, vertexShader: VERTEX, fragmentShader: fragment(), depthTest: false, depthWrite: false });
    this._mesh = new THREE.Mesh(this._geo, this._mat);
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -10;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._U.uPx.value = 2 / Math.max(1, _size.y); };
    scene.add(this._mesh);

    // state
    this._time = 0; this._barIndex = 0; this._beatInBar = 0; this._beatCount = 0;
    this._trackKey = null; this._rings = []; this._centredKey = null;
    this._grow = null; this._fadeOut = null; this._lastBarMs = null; this._barDur = 2.0; this._beatDur = 0.5; this._lastBarT = null; this._lastBeatT = null;
    this._palKey = null; this._vivid = FALLBACK_VIVID.map((c) => vdjColor(c));
    this._deep = DEEP.map((h) => new THREE.Color(h));
    this._flashG = 0;
    this._stats = { barChecks: 0, offSymmetry: 0, maxOffSteps: 0, ringFlashes: 0, snaps: 0, grows: 0, cuts: 0, drops: 0 };
  }

  // ------------------------------------------------------------ the mandala

  _geometry() {
    // the hole: round the cover block when the cover IS in the centre, the
    // true centre otherwise
    const sc = safeZoneUniforms.uSafeCenter.value, sh = safeZoneUniforms.uSafeHalfSize.value, st = safeZoneUniforms.uStageCenter.value;
    // (the centred cover sits a little above the exact centre: 0.0855 in the harness)
    const centred = sh.x > 0.02 && sh.y > 0.02 && Math.hypot(sc.x - st.x, sc.y - st.y) < 0.15;
    // the hole is centred on the composition centre (R19) and clears the cover's corners
    const r0 = centred ? Math.hypot(sh.x + Math.abs(sc.x - st.x), sh.y + Math.abs(sc.y - st.y)) * 1.02 : 0.05;
    return { centred, r0 };
  }

  // a mandala from a seed: ring count, widths growing outward, a motif and an
  // order per ring (no motif twice in a row; the order bounded so a sector is
  // never under MIN_SECTOR_PX at 1080 lines - no moire near the centre)
  _build(seed) {
    const r = rng(seed * 2654435761 + 17);
    const { r0, centred } = this._geometry();
    const n = 6 + Math.floor(r() * 5);
    const weights = Array.from({ length: n }, (_, i) => 1 + 0.18 * i);
    const sum = weights.reduce((a, b) => a + b, 0);
    let rr = r0;
    const rings = [];
    let prevMotif = -1;
    for (let i = 0; i < n; i++) {
      const w = (R_OUT - r0) * weights[i] / sum, rin = rr, rout = rr + w; rr = rout;
      const rm = (rin + rout) / 2, circPx = 2 * Math.PI * rm / (2 / 1080);
      // an order that keeps the sector in proportion with the band (half
      // sector / half band ~ 1.3: a petal stays a petal; with N = 6 on an
      // outer ring the petals were long flat arcs), never under MIN_SECTOR_PX
      const H = 0.42 * w, ratio = (N) => (Math.PI * rm / N * 0.92) / H;
      const allowed = ORDERS.filter((N) => circPx / N >= MIN_SECTOR_PX);
      const fit = allowed.filter((N) => Math.abs(Math.log(ratio(N) / 1.2)) < Math.log(1.9));
      const pool = fit.length ? fit : allowed.slice().sort((x, y) => Math.abs(Math.log(ratio(x) / 1.2)) - Math.abs(Math.log(ratio(y) / 1.2))).slice(0, 1);
      const N = pool.length ? pool[Math.floor(r() * pool.length)] : 4;
      let m; do { m = Math.floor(r() * MOTIFS.length); } while (m === prevMotif);
      if (this._override && this._override.i === i) m = MOTIFS.indexOf(this._override.m);
      prevMotif = m;
      rings.push({ rin, rout, N, motif: m, dir: i % 2 ? -1 : 1, steps: 0, moveK: 0, snapT: -9, start: r() * Math.PI * 2, flash: 0, reveal: 0, hue: i });
    }
    this._rings = rings;
    this._centredKey = centred;
  }

  // THE GROWTH. From nothing (the start of a track, a new mandala): the rings
  // appear one after another. On a section or a drop (regrow): each ring stays
  // until its turn, then is erased and drawn again by the compass - a wave of
  // redrawing leaves the centre and the mandala is never blank (from nothing,
  // at a ring every 2 s, 10 rings and 15-30 s sections left it empty most of
  // the time)
  _startGrowth(interval, regrow) {
    this._fadeOut = null;
    this._grow = { t0: this._time, interval, regrow: !!regrow };
    this._stats.grows++;
  }

  // the music's tempo, kept from the adapt events (works in ?adaptsim too)
  _tempo(a) {
    const t = this._time;
    if (a.beat) { if (this._lastBeatT != null) { const d = t - this._lastBeatT; if (d > 0.2 && d < 2) this._beatDur += (d - this._beatDur) * 0.3; } this._lastBeatT = t; }
    if (a.bar) { if (this._lastBarT != null) { const d = t - this._lastBarT; if (d > 0.8 && d < 8) this._barDur += (d - this._barDur) * 0.3; } this._lastBarT = t; }
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK_VIVID;
    const key = v.map(String).join('|');
    if (key === this._palKey) return;
    this._palKey = key;
    // R4: colours that must read apart sit at least 0.25 of the turn apart -
    // keep the cover's vivid colours that do, complete with rotations of the
    // first (a peach / pink / orange cover gave a monochrome mandala)
    const out = [], hue = (c) => { const h = {}; c.getHSL(h); return h.h; };
    for (const c of v.map((x) => vdjColor(x))) {
      if (out.every((o) => { const d = Math.abs(hue(o) - hue(c)); return Math.min(d, 1 - d) >= 0.25; })) out.push(c);
    }
    for (const dh of [1 / 3, 2 / 3]) if (out.length < 3) out.push(out[0].clone().offsetHSL(dh, 0, 0));
    this._vivid = out;
  }
  _ramp(list, u) {
    const n = list.length, x = ((u % 1) + 1) % 1 * n, i = Math.floor(x), f = sm(x - i);
    return _c1.copy(list[i % n]).lerp(list[(i + 1) % n], f);
  }

  // ------------------------------------------------------------ update

  update(deltaTime, profile) {
    this._applyPalette(profile);
    const paused = profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    if (paused) { this._push(); return; }                    // R11: a freeze (adapt freezes too at dt 0)
    const t = this._time += dt;
    this._tempo(a);

    // the base mandala: one per track (a seed from the track), rebuilt when
    // the cover moves in or out of the centre
    const key = this._forcedSeed != null ? 'seed' + this._forcedSeed : (profile.trackId || profile.title || 'none');
    const geo = this._geometry();
    if (key !== this._trackKey || geo.centred !== this._centredKey) {
      const first = this._trackKey == null || key !== this._trackKey;
      this._trackKey = key;
      this._seed = this._forcedSeed != null ? this._forcedSeed : hashStr(key);
      this._build(this._seed);
      this._startGrowth(this._growInterval(a), !first);
    }

    const intense = a.intensity;
    // the bar clock: whole bars from the events, the position inside the bar
    // from the beats (so every ring lands on its symmetry on the downbeat)
    if (a.bar) {
      this._barIndex++; this._beatInBar = 0;
      // the moves of the bar that just ended are complete: whole steps
      for (const rg of this._rings) { rg.steps += rg.moveK; rg.moveK = 0; }
      this._checkSymmetry();
      // this bar's movers: period P from adapt.motion, staggered ring by ring
      const m = a.motion, P = m < 0.42 ? 4 : m < 0.62 ? 2 : 1, k = m > 0.9 ? 2 : 1;
      this._rings.forEach((rg, i) => { rg.moveK = (this._barIndex + i) % P === 0 ? k : 0; });
      // a CUT on the bar (intense register): one ring gets a new motif, redrawn
      if (a.cutPolicy === 'bar' && this._rings.length && !this._grow) {
        const rg = this._rings[(this._barIndex * 7) % this._rings.length];
        let m2; do { m2 = Math.floor(Math.random() * MOTIFS.length); } while (m2 === rg.motif);
        rg.motif = m2; rg.reveal = 0; rg.redraw = { t0: t, dur: Math.max(0.2, this._beatDur) };
        this._stats.cuts++;
      }
    } else if (a.beat) this._beatInBar++;
    if (a.beat) {
      this._beatCount++;
      // the intense register: on each beat one ring flashes and turns one more step
      if (a.pulse > 0.35 && this._rings.length) {
        const rg = this._rings[this._beatCount % this._rings.length];
        rg.steps += 1; rg.snapT = t; rg.flash = Math.max(rg.flash, clamp01(a.pulse * 1.4));
        this._stats.snaps++; this._stats.ringFlashes++;
      }
    }
    // SECTION: the mandala grows again; DROP (only when the track has drops): one step together, a flash, regrown in a bar
    if (a.drop && a.dropReady) {
      for (const rg of this._rings) { rg.steps += 1; rg.snapT = t; rg.flash = 1; }
      if (this._comfort.requestFlash(t)) { this._flashG = 1; if (profile.director) profile.director.flash = Math.max(profile.director.flash || 0, VDJ_DROP_FLASH * 0.6); }
      this._startGrowth(this._barDur / Math.max(1, this._rings.length), true);
      this._stats.drops++;
    } else if (a.section) this._startGrowth(this._growInterval(a), true);

    // ring angles: whole steps + the eased part of this bar's move + the snap
    const barFrac = clamp01((this._beatInBar + (a.beatPhase || 0)) / 4);
    const mv = barFrac * barFrac * barFrac * (barFrac * (barFrac * 6 - 15) + 10);
    for (const rg of this._rings) {
      const snap = rg.snapT > -9 ? clamp01((t - rg.snapT) / 0.15) : 1;
      rg.angle = rg.dir * (rg.steps - (1 - sm(snap)) * (snap < 1 ? 1 : 0) + rg.moveK * mv) * Math.PI * 2 / rg.N;
      rg.flash *= Math.exp(-dt / 0.18);
    }
    // THE GROWTH: one ring after another, each drawn as a compass sweep
    if (this._grow) {
      const g = this._grow; let done = true;
      this._rings.forEach((rg, i) => {
        const u = (t - g.t0 - i * g.interval) / g.interval;
        rg.reveal = g.regrow && u < 0 ? 1 : clamp01(u);
        if (u < 1) done = false;
      });
      if (done) this._grow = null;
    }
    for (const rg of this._rings) if (rg.redraw) { rg.reveal = clamp01((t - rg.redraw.t0) / rg.redraw.dur); if (rg.reveal >= 1) rg.redraw = null; }

    // breath: ample and slow when calm, slight when intense
    const breathT = Math.max(2 * this._barDur, 3);
    this._breath = (this._breath || 0) + dt / breathT * Math.PI * 2;
    this._U.uBreathAmp.value = 0.03 * (1 - 0.6 * intense);
    this._U.uBreathPhase.value = this._breath;

    // comfort (R10): a crude mean luminance against adapt.lumaCap; the drop's flash
    this._flashG *= Math.exp(-dt / 0.15);
    const est = 0.05 + 0.05 * intense + 0.25 * this._flashG;
    this._U.uGain.value = Math.min(1, a.lumaCap / Math.max(est, 1e-3)) * this._comfort.measure(est, dt) * (1 + 0.6 * this._flashG);
    // the vdj RGB split, in proportion to the beat pulse (none on a ballad)
    const chroma = vdjBeatChroma(a.beatPhase || 0.99, this._beatDur);
    this._post.update(1 + (chroma - 1) * a.pulse);
    this._push();
  }

  // the uniforms of every ring, and its colour
  _push() {
    const U = this._U, a = this._a || { saturation: 0.65 };
    U.uCenter.value.copy(safeZoneUniforms.uStageCenter.value);
    U.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
    U.uCount.value = this._rings.length;
    const vividK = clamp01((a.saturation - 0.65) / 0.25), flow = this._time * 0.03;
    let fade = 1;
    if (this._fadeOut) { fade = 1 - clamp01((this._time - this._fadeOut.t0) / this._fadeOut.dur); if (fade <= 0) this._fadeOut = null; }
    this._rings.forEach((rg, i) => {
      const deep = this._ramp(this._deep, i * 0.21 - flow).clone();
      const vivid = this._ramp(this._vivid, i * 0.17 - flow);
      const c = deep.lerp(vivid, vividK);
      const growing = this._grow && this._fadeOut == null;
      U['uA' + i].value.set(rg.rin, rg.rout, rg.N, rg.motif);
      U['uB' + i].value.set(rg.angle || 0, this._fadeOut ? 1 : rg.reveal, rg.flash, rg.start);
      U['uC' + i].value.set(c.r, c.g, c.b, this._fadeOut ? fade : 1);
    });
  }

  _growInterval(a) {
    // a ring per beat at the top, one every 2 s when calm
    const k = sm((a.intensity - 0.35) / 0.4);
    return 2.0 + (Math.min(this._beatDur, 1) - 2.0) * k;
  }

  // criterion 2: on a downbeat every ring sits on a whole number of steps
  _checkSymmetry() {
    for (const rg of this._rings) {
      this._stats.barChecks++;
      const steps = rg.steps;
      const off = Math.abs(steps - Math.round(steps));
      if (off > 1e-9) { this._stats.offSymmetry++; this._stats.maxOffSteps = Math.max(this._stats.maxOffSteps, off); }
    }
  }

  getProbe() {
    const a = this._a || {};
    return {
      rings: this._rings.map((r) => ({ N: r.N, motif: MOTIFS[r.motif], rin: +r.rin.toFixed(3), rout: +r.rout.toFixed(3), steps: r.steps, angleSteps: +((r.angle || 0) * r.N / (Math.PI * 2)).toFixed(4), reveal: +r.reveal.toFixed(2) })),
      intensity: +(a.intensity || 0).toFixed(2), pulse: +(a.pulse || 0).toFixed(2), motion: +(a.motion || 0).toFixed(2), cut: a.cutPolicy, colour: a.colorMode,
      bar: this._barIndex, beat: this._beatCount, growing: !!this._grow, stats: Object.assign({}, this._stats), sim: this._adapt ? this._adapt.simulated : null
    };
  }

  dispose() {
    if (this._scene) { this._scene.remove(this._mesh); this._scene.background = this._prevBackground || null; }
    if (this._geo) this._geo.dispose();
    if (this._mat) this._mat.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__mandala === this) window.__mandala = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _c1 = new THREE.Color();
